// ======================== PWA INSTALL PROMPT ========================
// Composant pour proposer l'installation de l'app (Android + iOS)

import { useState, useEffect } from 'react';
import { useNetwork } from '../contexts/NetworkContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Download, X, Smartphone, Bell, Wifi, Share, Plus } from 'lucide-react';

// Detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Detect if in standalone mode (already installed)
const isInStandaloneMode = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
    window.navigator.standalone === true;
};

const PWAInstallPrompt = () => {
  const { 
    isInstallable, 
    isInstalled, 
    promptInstall,
    notificationPermission,
    requestNotificationPermission
  } = useNetwork();
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  const isiOSDevice = isIOS();
  const isStandalone = isInStandaloneMode();
  
  useEffect(() => {
    // Check if user has dismissed the prompt before
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedDate = new Date(wasDismissed);
      const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      
      // Show again after 7 days
      if (daysSince > 7) {
        setDismissed(false);
      } else {
        setDismissed(true);
      }
    }
    
    // Show prompt after 5 seconds if installable (Android) or iOS not in standalone
    const canShowPrompt = (isInstallable || (isiOSDevice && !isStandalone)) && !isInstalled && !dismissed;
    
    if (canShowPrompt) {
      const timer = setTimeout(() => setShowPrompt(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled, dismissed, isiOSDevice, isStandalone]);
  
  const handleInstall = async () => {
    if (isiOSDevice) {
      // Show iOS instructions
      setShowIOSInstructions(true);
    } else {
      const success = await promptInstall();
      if (success) {
        setShowPrompt(false);
        // Request notification permission after install
        if (notificationPermission !== 'granted') {
          requestNotificationPermission();
        }
      }
    }
  };
  
  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };
  
  if (!showPrompt || isInstalled || isStandalone) {
    return null;
  }
  
  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center p-4">
        <Card className="w-full max-w-md animate-in slide-in-from-bottom duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-blue-500" />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleDismiss}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <h3 className="text-lg font-bold mb-2">
              Installation sur iPhone
            </h3>
            
            <p className="text-sm text-muted-foreground mb-4">
              Suivez ces étapes pour installer ALDRIN'S sur votre iPhone :
            </p>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium">Appuyez sur le bouton Partager</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <Share className="w-5 h-5" />
                    <span>(carré avec flèche vers le haut)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium">Faites défiler et appuyez sur</p>
                  <div className="flex items-center gap-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 mt-1">
                    <Plus className="w-5 h-5" />
                    <span className="font-medium">Sur l'écran d'accueil</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium">Appuyez sur "Ajouter"</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    L'icône ALDRIN'S apparaîtra sur votre écran d'accueil
                  </p>
                </div>
              </div>
            </div>
            
            <Button
              className="w-full"
              onClick={handleDismiss}
            >
              J'ai compris
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center p-4">
      <Card className="w-full max-w-md animate-in slide-in-from-bottom duration-300">
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Download className="w-6 h-6 text-primary" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleDismiss}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <h3 className="text-lg font-bold mb-2">
            Installer ALDRIN'S
          </h3>
          
          <p className="text-sm text-muted-foreground mb-4">
            Installez l'application sur votre {isiOSDevice ? 'iPhone' : 'appareil'} pour un accès rapide.
          </p>
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-sm">
              <Smartphone className="w-4 h-4 text-primary" />
              <span>Icône sur l'écran d'accueil</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Bell className="w-4 h-4 text-primary" />
              <span>Notifications push</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Wifi className="w-4 h-4 text-primary" />
              <span>Fonctionne hors ligne</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDismiss}
            >
              Plus tard
            </Button>
            <Button
              className="flex-1"
              onClick={handleInstall}
            >
              {isiOSDevice ? (
                <>
                  <Share className="w-4 h-4 mr-2" />
                  Comment faire ?
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Installer
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
